import type React from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { Tooltip } from '@components/ui/Tooltips'
import { OverflowTextWithTooltip } from '@oldComponents/OverflowTextWithTooltip'
import { LinkIcon } from '@oldComponents/ui/SvgIcons'

import Badge, { BadgeVariant } from './Badge'

interface ContainerDivProps {
  maxWidth: number | '100%'
}

const StyledOverflowText = styled(OverflowTextWithTooltip)`
  margin-right: 4px;
`

const ContainerDiv = styled.div<ContainerDivProps>`
  display: flex;
  align-items: center;
  max-width: ${({ maxWidth }) => (typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth)};
  gap: 2px;
`

interface InvoiceBadgeProps {
  children?: React.ReactNode
  differentWarning?: NonNullable<React.ReactNode> | false
  badges: InvoiceBadgeValue[]
  maxWidth?: number | '100%'
}

/**
 * This component render different badges next to its children when badges property containes the relevant badge key values
 * @param {InvoiceBadgeProps} props
 * @param props.children (optional) element to display before the badges,
 * @param props.differentWarning (optional) extra element to display,
 * @param props.badges list of displayable badges
 * @param props.maxWidth (optional) max width of the container
 * @returns
 */
function InvoiceBadge({ children, badges, differentWarning, maxWidth = 240 }: InvoiceBadgeProps) {
  return (
    <ContainerDiv maxWidth={maxWidth}>
      {children && <StyledOverflowText withPortal>{children}</StyledOverflowText>}
      {differentWarning}
      {badges.includes('new') && (
        <Badge variant={BadgeVariant.INFO}>
          <FormattedMessage id="badges.notSeenYet" defaultMessage="Új" />
        </Badge>
      )}
      {badges.includes('updated') && (
        <Badge variant={BadgeVariant.INFO}>
          <FormattedMessage id="badges.updated" defaultMessage="Frissült" />
        </Badge>
      )}
      {badges.includes('expiring') && (
        <Badge variant={BadgeVariant.ERROR}>
          <FormattedMessage id="badges.expireSoon" defaultMessage="Lejár" />
        </Badge>
      )}
      {badges.includes('expired') && (
        <Badge variant={BadgeVariant.ERROR}>
          <FormattedMessage id="badges.expired" defaultMessage="Lejárt" />
        </Badge>
      )}
      {badges.includes('nav_link') && (
        <Tooltip
          title={
            <FormattedMessage
              id="badges.tooltips.navLink"
              defaultMessage="Ezt a számlát már a NAV rendszeréből is szinkronizáltuk"
            />
          }
          withPortal
        >
          <Badge variant={BadgeVariant.NAV}>
            <>
              NAV&nbsp;
              <LinkIcon />
            </>
          </Badge>
        </Tooltip>
      )}
      {badges.includes('nav_without_preview') && (
        <Tooltip
          title={
            <FormattedMessage
              id="badges.tooltips.navPreview"
              defaultMessage="Ehhez a NAV-tól származó számlához még nem tartozik számlakép"
            />
          }
          withPortal
        >
          <Badge variant={BadgeVariant.NAV}>
            <FormattedMessage id="badges.nav.noArtifact" defaultMessage="Nincs számlakép" />
          </Badge>
        </Tooltip>
      )}
      {badges.includes('missing') && (
        <Badge variant={BadgeVariant.ERROR}>
          <FormattedMessage id="badge.deficient.data" defaultMessage="Adathiányos" />
        </Badge>
      )}
    </ContainerDiv>
  )
}

export default InvoiceBadge
